import React, { useState } from 'react';
import { motion } from 'motion/react';
import { X, Zap, CheckCircle2, ChevronLeft } from 'lucide-react';
import { moduleApi } from '../../lib/api';

const PLATFORMS: { key: string; label: string }[] = [
    { key: 'zenchef', label: 'Zenchef' },
    { key: 'thefork', label: 'TheFork Manager' },
    { key: 'resengo', label: 'Resengo' },
    { key: 'autre', label: 'Autre' },
];

export function AutomationRequestModal({ onClose }: { onClose: () => void }) {
    const [step, setStep] = useState<'platform' | 'apiKey' | 'success'>('platform');
    const [platform, setPlatform] = useState('');
    const [apiKey, setApiKey] = useState('');
    const [sending, setSending] = useState(false);
    const [error, setError] = useState('');

    const handleSend = async () => {
        if (!apiKey.trim()) {
            setError('Merci de renseigner votre clé API.');
            return;
        }
        setSending(true);
        setError('');
        try {
            await moduleApi.createExceptionServiceAutomationRequest({ platform, api_key: apiKey.trim() });
            setStep('success');
        } catch (e: any) {
            setError(e?.message || "Erreur lors de l'envoi de la demande.");
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
            <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="bg-white dark:bg-surface border border-border rounded-2xl w-full max-w-md shadow-2xl overflow-hidden"
            >
                <div className="px-6 py-4 border-b border-border flex items-center justify-between">
                    <h3 className="text-lg font-bold text-slate-900 dark:text-content flex items-center gap-2">
                        <Zap size={18} className="text-brand-500" /> Saisie automatique des réservations
                    </h3>
                    <button onClick={onClose} className="text-content-muted hover:text-gray-700 dark:hover:text-slate-900 transition-colors">
                        <X size={20} />
                    </button>
                </div>

                <div className="p-6 space-y-4">
                    {step === 'platform' && (
                        <>
                            <p className="text-sm text-content-muted">Quelle plateforme de réservation utilisez-vous ?</p>
                            <div className="grid grid-cols-2 gap-2">
                                {PLATFORMS.map((p) => (
                                    <button
                                        key={p.key}
                                        onClick={() => setPlatform(p.key)}
                                        className={`px-4 py-3 rounded-xl border text-sm font-bold transition-all ${
                                            platform === p.key
                                                ? 'border-brand-500 bg-brand-500/10 text-brand-600 dark:text-brand-400'
                                                : 'border-border text-content-muted hover:text-content hover:border-white/20'
                                        }`}
                                    >
                                        {p.label}
                                    </button>
                                ))}
                            </div>
                            <button
                                onClick={() => setStep('apiKey')}
                                disabled={!platform}
                                className="w-full px-5 py-3 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg font-bold disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                            >
                                Continuer
                            </button>
                        </>
                    )}

                    {step === 'apiKey' && (
                        <>
                            <button onClick={() => setStep('platform')} className="flex items-center gap-1 text-xs text-content-muted hover:text-content transition-colors">
                                <ChevronLeft size={14} /> Retour
                            </button>
                            <div className="space-y-1.5">
                                <label className="text-xs font-bold text-content-muted uppercase tracking-widest">
                                    Clé API {PLATFORMS.find((p) => p.key === platform)?.label}
                                </label>
                                <input
                                    type="text"
                                    autoFocus
                                    value={apiKey}
                                    onChange={(e) => setApiKey(e.target.value)}
                                    placeholder="Collez votre clé API ici"
                                    className="w-full bg-surface-soft border border-border rounded-xl px-4 py-3 text-sm text-content outline-none focus:border-brand-500 transition-colors"
                                />
                                <p className="text-xs text-content-muted italic">
                                    Cette clé sera transmise à notre équipe technique pour configurer l'automatisation de votre côté.
                                </p>
                            </div>
                            {error && <p className="text-xs text-red-400 font-semibold">{error}</p>}
                            <button
                                onClick={handleSend}
                                disabled={sending}
                                className="w-full px-5 py-3 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg font-bold disabled:opacity-50 transition-all"
                            >
                                {sending ? 'Envoi...' : 'Envoyer'}
                            </button>
                        </>
                    )}

                    {step === 'success' && (
                        <div className="text-center py-4 space-y-3">
                            <CheckCircle2 size={40} className="mx-auto text-emerald-500" />
                            <p className="text-sm text-content font-semibold">Votre demande a été transmise à notre équipe technique.</p>
                            <p className="text-sm text-content-muted">Nous revenons vers vous dans les plus brefs délais.</p>
                            <button onClick={onClose} className="mt-2 px-5 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg font-bold hover:opacity-90 transition-all">
                                Fermer
                            </button>
                        </div>
                    )}
                </div>
            </motion.div>
        </div>
    );
}
